import { api, ApiError } from '../network/api';
import type {
  AdminCapabilities, AdminInvoiceSettings, AdminListQuery, AdminOrdersQuery, AdminOrdersResponse, AdminPage,
  AdminPaymentConfig, AdminPaymentConfigPatch, AdminRefundRequest, AdminRefundResult, AdminWithdrawalsQuery,
  BillingAddressDto, BillingAddressView, CatalogueResponse, CreateDepositRequest, CreateDepositResponse,
  CreateWithdrawalRequest, CreateWithdrawalResponse, DepositOrder, PaymentMethodsResponse, PlayerInvoice, Withdrawal,
  WithdrawalLimits,
} from './types';

/** Error thrown by every function here. `code` is the backend error code (e.g. 'KYC_REQUIRED'). */
export class PaymentApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'PaymentApiError';
  }

  /** Per-field messages for BILLING_ADDRESS_INVALID. */
  get fieldErrors(): Record<string, string> {
    if (this.code !== 'BILLING_ADDRESS_INVALID' || !this.details) return {};
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(this.details)) if (typeof v === 'string') out[k] = v;
    return out;
  }
}

/** Convert whatever the core api client threw into a PaymentApiError. */
export function toPaymentError(err: unknown): PaymentApiError {
  if (err instanceof PaymentApiError) return err;
  if (err instanceof ApiError) {
    const body = (err.details ?? {}) as { details?: unknown; message?: unknown };
    const details = body.details && typeof body.details === 'object'
      ? (body.details as Record<string, unknown>)
      : undefined;
    const message = Array.isArray(body.message) ? body.message.join(', ') : err.message;
    return new PaymentApiError(err.status, err.code, message, details);
  }
  return new PaymentApiError(0, 'NETWORK_ERROR', err instanceof Error ? err.message : 'Request failed');
}

async function call<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    throw toPaymentError(e);
  }
}

const qs = (params: Record<string, string | number | undefined>): string => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

// ─── Player ─────────────────────────────────────────────────────────

export const getPaymentMethods = () => call(() => api.get<PaymentMethodsResponse>('/payments/methods'));

export const getCatalogue = () => call(() => api.get<CatalogueResponse>('/payments/catalogue'));

export const createDeposit = (body: CreateDepositRequest) =>
  call(() => api.post<CreateDepositResponse>('/payments/deposits', body));

export const listDeposits = (take?: number) =>
  call(() => api.get<DepositOrder[]>(`/payments/deposits${qs({ take })}`));

export const getDeposit = (orderId: string) =>
  call(() => api.get<DepositOrder>(`/payments/deposits/${encodeURIComponent(orderId)}`));

export const getBillingAddress = () => call(() => api.get<BillingAddressView>('/payments/billing-address'));

export const saveBillingAddress = (body: Partial<BillingAddressDto>) =>
  call(() => api.put<BillingAddressView>('/payments/billing-address', body));

export const getWithdrawalLimits = () => call(() => api.get<WithdrawalLimits>('/payments/withdrawals/limits'));

export const listWithdrawals = () => call(() => api.get<Withdrawal[]>('/payments/withdrawals'));

export const requestWithdrawal = (body: CreateWithdrawalRequest) =>
  call(() => api.post<CreateWithdrawalResponse>('/payments/withdrawals', body));

export const listInvoices = (page?: number) =>
  call(() => api.get<{ items: PlayerInvoice[]; total: number; page: number; pageSize: number }>(`/invoices${qs({ page })}`));

/** Same-origin download link (the session cookie authorises it). */
export const invoiceDownloadUrl = (number: string, apiBase = '/api') =>
  `${apiBase.replace(/\/$/, '')}/invoices/${encodeURIComponent(number)}/download`;

// ─── Admin ──────────────────────────────────────────────────────────

export const adminGetPaymentConfig = () => call(() => api.get<AdminPaymentConfig>('/admin/payments/config'));

export const adminPatchPaymentConfig = (patch: AdminPaymentConfigPatch) =>
  call(() => api.patch<AdminPaymentConfig>('/admin/payments/config', patch));

export const adminListOrders = (q: AdminOrdersQuery = {}) =>
  call(() => api.get<AdminOrdersResponse>(`/admin/payments/orders${qs({ ...q })}`));

export const adminResolveOrder = (orderId: string, action: 'credit' | 'dismiss', idempotencyKey?: string) =>
  call(() => api.post<unknown>(`/admin/payments/orders/${encodeURIComponent(orderId)}/resolve`, { action, idempotencyKey }));

export const adminRefundOrder = (orderId: string, body: AdminRefundRequest) =>
  call(() => api.post<AdminRefundResult>(`/admin/payments/orders/${encodeURIComponent(orderId)}/refund`, body));

export const adminListPayments = (q: AdminListQuery = {}) =>
  call(() => api.get<AdminPage>(`/admin/payments${qs({ ...q })}`));

export const adminListRefunds = (q: AdminListQuery = {}) =>
  call(() => api.get<AdminPage>(`/admin/payments/refunds${qs({ ...q })}`));

export const adminListCreditNotes = (q: AdminListQuery = {}) =>
  call(() => api.get<AdminPage>(`/admin/payments/credit-notes${qs({ ...q })}`));

export const adminListWebhookEvents = (q: AdminListQuery & { orderId?: string; kind?: 'deposit' | 'payout' } = {}) =>
  call(() => api.get<AdminPage>(`/admin/payments/webhook-events${qs({ ...q })}`));

export const adminGetInvoiceSettings = () => call(() => api.get<AdminInvoiceSettings>('/admin/invoices/settings'));

export const adminPatchInvoiceSettings = (patch: Partial<AdminInvoiceSettings>) =>
  call(() => api.patch<AdminInvoiceSettings>('/admin/invoices/settings', patch));

export const adminRedateInvoice = (number: string, issuedAt: string, reason: string) =>
  call(() => api.patch<unknown>(`/admin/invoices/${encodeURIComponent(number)}/date`, { issuedAt, reason }));

export const adminGetCapabilities = () => call(() => api.get<AdminCapabilities>('/admin/meta/capabilities'));

export const adminListWithdrawals = (q: AdminWithdrawalsQuery = {}) =>
  call(() => api.get<unknown>(`/admin/withdrawals${qs({ ...q })}`));

/** Records a PENDING/PROCESSING payout as paid (never sends money). */
export const adminApproveWithdrawal = (id: string, note?: string, idempotencyKey?: string) =>
  call(() => api.post<unknown>(`/admin/withdrawals/${encodeURIComponent(id)}/approve`, { note, idempotencyKey }));

export const adminRejectWithdrawal = (id: string, reason: string) =>
  call(() => api.post<unknown>(`/admin/withdrawals/${encodeURIComponent(id)}/reject`, { reason }));

export const adminCheckWithdrawalStatus = (id: string) =>
  call(() => api.post<unknown>(`/admin/withdrawals/${encodeURIComponent(id)}/check-status`));

export const adminResolveWithdrawal = (id: string, state: 'APPROVED' | 'DECLINED', note?: string) =>
  call(() => api.post<unknown>(`/admin/withdrawals/${encodeURIComponent(id)}/resolve`, { state, note }));
